import React, { ReactElement, useEffect, useState, useCallback } from "react";
import { Route, Routes } from "react-router-dom";
import Layout from "./pages";
import Login from "./pages/login";
import NotFound from "./pages/missing_page";
import RequireAuth from "./pages/require_auth";
import { OrderedCafe } from "./pages/users/ordered";
import { UserManagementCafe } from "./pages/admin/user_management";
import EditUser from "./pages/admin/user_management/edit_user";
import ClosePage from "./pages/cafe_close";
import GamePage from "./pages/game/GamePage";
import MenuBar from "./pages/users/menu_bar";
import MenuCafe from "./pages/users/menu_cafe";
import MenuDessert from "./pages/users/menu_dessert";
import { StatusOrderCafe } from "./pages/users/status_order";
import { getLocalStorage } from "./api/axios";
import { getUserMe } from "./api/user.api";

import { getCafeStatus } from "./api/cafe.api";
import { User } from "./interfaces/user";
import { useDispatch } from "react-redux";
import RouteContext from "./context/RouteContext";
import AppOutlet from "./components/AppOutlet";
import { BaristaDashboard } from "./pages/barista/dashboard";
import EditMenu from "./pages/barista/modal_page/editMenu";
import AddMenu from "./pages/barista/modal_page/addMenu";
import AddCategory from "./pages/barista/modal_page/addCategory";
import EditCategory from "./pages/barista/modal_page/editCategory";
import { AdminDashboard } from "./pages/admin/dashboard_admin";
import { BaristaHistory } from "./pages/barista/history";
import { BaristaOrdering } from "./pages/barista/ordering";
import { FormOrder } from "./pages/users/menu_cafe/components/formOrder";
import { FormOrderBar } from "./pages/users/menu_bar/components/formOrderBar";
import { FormOrderDessert } from "./pages/users/menu_dessert/components/formOrderDessert";
import EachCategory from "./pages/barista/menuManagement/components/eachCategory";
import { MenuManagement } from "./pages/barista/menuManagement";
import { DessertManagement } from "./pages/barista/dessertManagement";
import CreateAccount from "./pages/create";
import ForgotPassword from "./pages/forgot_password";
import ResetPassword from "./pages/reset_password";
import OrderAtBar from "../src/pages/order_bar";
import TeamCredit from "./team-credit";
import { BaristaStats } from "./pages/barista/stats";
import { AdminStats } from "./pages/admin/stats";
import { AdminBroadcasts } from "./pages/admin/broadcasts";
import ThemePicker from "./pages/admin/theme";
import AdminFridaySpecial from "./pages/admin/friday_special";
import AdminSettings from "./pages/admin/settings";
import AdminAuditLog from "./pages/admin/audit_log";
import AvatarEditorPage from "./pages/profile/AvatarEditor";

// Keep the showcase module out of ordinary production bundles entirely.
const HalloweenShowcase = process.env.REACT_APP_HALLOWEEN_PREVIEW === "true"
  ? React.lazy(() => import("./theme/HalloweenShowcase"))
  : null;

const App: React.FC = (): ReactElement => {
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();


  const storeRudux = useCallback((response: User) => {
    dispatch({
      type: "LOGIN",
      payload: response,
    });
  }, [dispatch]);

  const checkLocalStorage = useCallback(async () => {
    const accessToken = getLocalStorage("access_token").now_token;
    const refreshToken = getLocalStorage("refresh_token").now_token;

    if (accessToken || refreshToken) {
      // logic to fetch user
      try {
        const user = await getUserMe({
          access_token: accessToken || "",
        });
        const cafeStatus = await getCafeStatus();
        const responsepost: User = {
          login: true,
          id: user?.id,
          sub: "auth_google",
          email: user?.email,
          name: user?.name,
          role: user?.role,
          isBarOpen: cafeStatus.isBarOpen,
          isCafeOpen: cafeStatus.isCafeOpen,
        };
        storeRudux(responsepost);
      } catch (error) {
        console.log("Failed to fetch user in checkLocalStorage", error);
      }
    }
  }, [storeRudux]);

  useEffect(() => {
    document.title = "Woxa Cafe";
  });

  useEffect(() => {
    (async () => {
      try {
        await checkLocalStorage();
      } catch (error) {
        console.log("Initialization error:", error);
        // If error occurs (and not handled by interceptor redirect), 
        // we might want to ensure we don't get stuck in loading state.
        // But usually interceptor handles auth errors.
      } finally {
        setIsInitialized(true);
      }
    })();
  }, [checkLocalStorage]);

  return (
    <RouteContext.Provider
      value={{
        isInitialized,
      }}
    >
      <Routes>
        {HalloweenShowcase && <Route path="/halloween-art" element={<React.Suspense fallback={null}><HalloweenShowcase /></React.Suspense>} />}
        <Route path="/" element={<MenuCafe />}></Route>
        {isInitialized ? (
          <>
            {/* public routes */}
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="create" element={<CreateAccount />} />
            <Route path="unauthorized" element={<NotFound />} />
            <Route path="order_bar" element={<OrderAtBar />} />
            <Route path="team-credit" element={<TeamCredit />} />
            {/* own profile + mini games — every signed-in role, including barista */}
            <Route element={<RequireAuth allowedRoles={["user", "barista", "admin", "dev"]} />}>
              <Route path="profile/avatar" element={<AvatarEditorPage />} />
              <Route path="game" element={<GamePage />} />
            </Route>
            {/* protect routes for user cafe (also open to admin/dev for QA) */}
            <Route element={<RequireAuth allowedRoles={["user", "admin", "dev"]} />}>
              <Route path="menuCafe" element={<MenuCafe />} />
              <Route path="menuDessert" element={<MenuDessert />} />
              <Route path="menubar" element={<MenuBar />} />
              <Route path="statusOrder" element={<StatusOrderCafe />} />
              <Route path="ordered" element={<OrderedCafe />} />
              <Route path="close-all" element={<ClosePage />} />
              <Route path="form/:menuName/:menuID" element={<FormOrder />} />
              <Route path="form-bar/:menuName/:menuID/:description" element={<FormOrderBar />} />
              <Route path="form-dessert/:menuName/:menuID" element={<FormOrderDessert />} />

              <Route path="*" element={<NotFound />} />
            </Route>

            {/* protect routes for admin cafe (admin + dev) */}
            <Route element={<RequireAuth allowedRoles={["admin", "dev"]} />}>
              <Route path="menuCafe-admin" element={<MenuCafe />} />
              <Route path="menubar-admin" element={<MenuBar />} />
              <Route path="statusOrder-admin" element={<StatusOrderCafe />} />
              <Route path="ordered-admin" element={<OrderedCafe />} />
              <Route path="dashboard-admin" element={<AdminDashboard />} />
              <Route path="userManagement" element={<UserManagementCafe />} />
              <Route path="admin/user/:id/edit" element={<EditUser />} />
              <Route
                path="form-admin/:menuName/:menuID"
                element={<FormOrder />}
              />
              <Route path="form-bar-admin/:menuName/:menuID/:description" element={<FormOrderBar />} />
              <Route
                path="form-dessert-admin/:menuName/:menuID"
                element={<FormOrderDessert />}
              />
              {/* <Route
                path="dashboard-stats-admin"
                element={<AdminStats />}
              /> */}
              <Route path="dashboard-stats-admin" element={<AdminStats />} />
              <Route path="admin/settings" element={<AdminSettings />} />
              <Route path="admin/audit-log" element={<AdminAuditLog />} />
              <Route path="admin/theme" element={<ThemePicker />} />
              <Route path="profile/avatar-admin" element={<AvatarEditorPage />} />

              <Route path="/" element={<Layout />}>
                <Route path="close-all-admin" element={<ClosePage />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* protect routes for staff (barista, admin, dev) */}
            <Route element={<RequireAuth allowedRoles={["barista", "admin", "dev"]} />}>
              <Route path="ordering" element={<BaristaOrdering />} />
              <Route path="history" element={<BaristaHistory />} />
              <Route path="menuManagement" element={<MenuManagement />} />
              <Route path="dessertManagement" element={<DessertManagement />} />
              <Route
                path="menuManagement/:categoryName/:categoryId"
                element={<EachCategory />}
              />
              <Route path="edit-menu/:menuId" element={<EditMenu />} />
              <Route
                path="edit-category/:categoryId"
                element={<EditCategory />}
              />
              <Route path="add-menu/:categoryId" element={<AddMenu />} />
              <Route path="add-category" element={<AddCategory />} />
              <Route path="dashboard-cafe" element={<BaristaDashboard />} />
              <Route path="broadcasts-admin" element={<AdminBroadcasts />} />
              <Route path="friday-special-admin" element={<AdminFridaySpecial />} />
              <Route path="dashboard-stats" element={<BaristaStats />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            {/* catch all */}
            <Route path="*" element={<NotFound />}></Route>
          </>
        ) : (
          <>
            <Route path="login" element={<Login />} />
            <Route path="create" element={<CreateAccount />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            {/* While initializing, show loader on any path to avoid blank screen on deep links */}
            <Route path="*" element={<AppOutlet />} />
          </>
        )}
      </Routes>
    </RouteContext.Provider>
  );
};

export default App;
